import React, { useContext, useReducer } from 'react';
import BuilderReducerNew from './builderReducerNew';
import BuilderContextNew from './builderContextNew';
import AuthContext from '@context/auth/AuthContext';
import { EVENT_NAMES } from '@constants/trackingConstants';
import { v4 as uuidv4 } from 'uuid';
import { trackerService } from '@api/tracker';

function BuilderStateNew(props) {
  const authContext = useContext(AuthContext);

  //Fields are going to be used to store selected Attributes
  // sqlWhere to store query
  const initialState = {
    sqlWhere: '',
    display: [],
    numberOfGroups: 1,
    // this is used to keep track of builder type used (Simple or Advanced) in other modules
    advanced: false,
    qbFields: {},
    // this is used to show/hide errors if any fields are left blank
    showError: false,
    logicalOperators: [],
    groups: [
      {
        id: 'main',
        type: 'group',
        children: [{ id: 'condition-1', type: 'condition', children: [] }],
      },
    ],
    conditions: {
      'condition-1': {
        id: 'condition-1',
        attribute: [],
        variables: [],
        option: 'include',
      },
    },
    operators: {
      'operator-1': {
        id: 'operator-1',
        type: 'operator',
        selected: true,
        filtered: true,
        value: 'AND',
      },
    },
    workflowId: null,
  };

  const [state, dispatch] = useReducer(BuilderReducerNew, initialState);

  const setFields = (fields) => {
    dispatch({ type: 'SET_FIELD', payload: fields });
  };

  const setDisplay = (display) => {
    dispatch({ type: 'SET_DISPLAY', payload: display });
  };

  // This method will refresh the AdvancedBuilder internal state, setting
  // excluding fields that are specified after ...initialState.
  // the reason for that is that some of these fields are used in other modules
  // for example, qbFields is used in Customer Discovery and needs to persist its state
  // so that Advanced audience selector works properly.
  const refreshAdvancedQB = () => {
    dispatch({
      type: 'REFRESH',
      payload: {
        ...initialState,
        qbFields: state.qbFields,
        display: state.display,
        advanced: state.advanced,
        workflowId: state.workflowId,
      },
    });
  };

  const resetAdvancedQB = () => {
    dispatch({
      type: 'REFRESH',
      payload: {
        ...initialState,
      },
    });
  };

  /**
   * Used to add a new condition to query.
   * @param {*} condition  new condition in query builder using this format: {id: id, attribute: string, variables: array, option: string, empty: boolens}
   * @param {*} operation takes OR/AND. AND by default
   */
  const addCondition = (conditionId, groupId) => {
    dispatch({ type: 'ADD_CONDITION', payload: { conditionId, groupId } });
  };
  /**
   * Used to remove condition from query. Not finished yet. LogicalOperators will be implemented later.
   * @param {*} id of condition (a.k.a group) to be removed.
   */
  const removeCondition = (conditionId, groupId) => {
    dispatch({ type: 'REMOVE_CONDITION', payload: { conditionId, groupId } });
  };
  /**
   * Used to modify existing condition in query builder.
   * @param {*} id of condition/group to modify
   * @param {*} parameter of condition to be modified as an object: {key: newValue}
   */
  const modifyCondition = (id, parameter) => {
    dispatch({ type: 'MODIFY_CONDITION', payload: { id, parameter } });
  };

  /**
   * Function used to set and update values in logicalOperators array
   * @param {*} id index of operator that will be changed
   * @param {*} value new value of operator to change
   */
  const setLogicalOperator = (id, value) => {
    dispatch({ type: 'SET_LOGICAL_OPERATOR', payload: { id, value } });
  };
  /**
   * Function used to delete value in logicalOperators array
   * @param {*} id index of operator that will be changed
   */
  const deleteLogicalOperator = (id) => {
    dispatch({ type: 'DELETE_LOGICAL_OPERATOR', payload: id });
  };

  const setGroups = (group, groupIndex, currentList) => {
    dispatch({
      type: 'SET_GROUPS',
      payload: { group, groupIndex, currentList },
    });
  };

  const addGroup = (groupIndex) => {
    dispatch({ type: 'ADD_GROUP', payload: groupIndex });
  };

  const removeGroup = (groupIndex) => {
    dispatch({ type: 'REMOVE_GROUP', payload: groupIndex });
  };

  const updateOperator = (value, id) => {
    dispatch({ type: 'UPDATE_OPERATOR', payload: { value, id } });
  };

  const setShowError = (value) => {
    dispatch({ type: 'SET_SHOWERROR', payload: value });
  };

  const setAudienceBuilderType = (value) => {
    dispatch({ type: 'SET_AUDIENCE_BUILDER', payload: value });
  };

  // A function which will run after "Finish" button is pressed.
  // It will return true if either attribute or variable for any condition is empty
  // this is required to validate if any fields are left empty on Advanced Builder.
  const checkEmptyConditions = () => {
    let result = false;
    for (const conditionName of Object.keys(state.conditions)) {
      const conditionUnderCheck = state.conditions[conditionName];
      if (
        !conditionUnderCheck.attribute.code ||
        conditionUnderCheck.variables.length === 0
      ) {
        result = true;
        break;
      }
    }
    // if (state.groups.length === 0) {
    //   result = true;
    // }
    return result;
  };
  // sqlWhere: (dem_01 IN (1, 2, 3) AND (dem_02 IN (12, 13) OR dem_02 IN (12)) AND dem_01 IN (1))
  // qbFields:
  // Object of Objects: dem_01: {fieldSettings: {…}, label: "Age", operators: Array…}
  //  most interesting is fieldSetting:
  //  it is an object which has one nested array: listValues: Array(11)
  //  this is array of object and each object looks like:
  //  {title: "18 to 19 years", value: 1}
  //  dem_02: {fieldSettings: {…}, label: "Gender", operators: Ar…}
  const getSQLWhere = (qbFields) => {
    const groups = state.groups;
    // Check if there are empty conditions and set ShowError to true;
    const emptyConditions = checkEmptyConditions();
    setShowError(emptyConditions);
    if (emptyConditions) {
      return -1;
    }
    // Array for sqlWhere statement, which we will convert into string at the last step.
    const sqlWhereArray = [];
    groups.forEach((group) => {
      // logic if current element in groups has type "group", i.e. contain children
      if (group.type === 'group') {
        const children = group.children;
        // get length of children to know where to close group bracket
        const childrenSize = children.length - 1;
        if (childrenSize > 1) {
          sqlWhereArray.push('(');
        }
        children.forEach((children, index) => {
          if (children.type === 'condition') {
            const currentCondition = state.conditions[children.id];
            // get attribute for the current child: i.e. "dem_01"
            const attribute = currentCondition.attribute.code;
            // array of variables, like: ["Age - 18 to 19 years", "Age - 20 to 24 years"]
            const variables = currentCondition.variables;
            const codifiedVariables = returnCodes(
              qbFields,
              attribute,
              variables
            );
            const option =
              currentCondition.option === 'include' ? 'IN' : 'NOT IN';
            const codifiedVarsConcat = codifiedVariables.join(', ');
            const conditionToAdd = attribute.concat(
              ' ',
              option,
              ' ',
              '(',
              codifiedVarsConcat,
              ')'
            );
            sqlWhereArray.push(conditionToAdd);
            if (childrenSize > 1 && index === childrenSize) {
              sqlWhereArray.push(')');
            }
          } else {
            sqlWhereArray.push(' ' + state.operators[children.id].value + ' ');
          }
        });
      } else if (group.type === 'condition') {
        const currentCondition = state.conditions[group.id];
        // get attribute for the current child: i.e. "dem_01"
        const attribute = currentCondition.attribute.code;
        // array of variables, like: ["Age - 18 to 19 years", "Age - 20 to 24 years"]
        const variables = currentCondition.variables;
        //check if any of fields are blank and skip this condition
        if (attribute.length === 0 || variables.length === 0) {
          return;
        }
        const codifiedVariables = returnCodes(qbFields, attribute, variables);
        const option = currentCondition.option === 'include' ? 'IN' : 'NOT IN';
        const conditionToAdd = attribute.concat(
          ' ',
          option,
          ' ',
          '(',
          codifiedVariables.join(', '),
          ')'
        );
        sqlWhereArray.push(conditionToAdd);
      } else {
        sqlWhereArray.push(' ' + state.operators[group.id].value + ' ');
      }
    });
    const sqlWhere = '(' + sqlWhereArray.join('') + ')';
    dispatch({ type: 'SET_SQL', payload: sqlWhere });
    return sqlWhere;
  };
  // "MONTREAL, QC"
  // geo_city IN ('ABBOTSFORD')
  // Function to return codified variables (ie. number from 1 to ~ 4400) instead of text
  function returnCodes(qbFields, attribute, variables) {
    const variablesForAttribute = qbFields[attribute]?.fieldSettings.listValues;
    const codifiedVariables = variables.map((variable) => {
      // console.log("variable", variable, attribute)
      const indexOfDash = variable.indexOf(' - ');
      let searchVar = variable.split(' - ')?.slice(-1)[0];
      if (indexOfDash === -1) {
        searchVar = variable;
      }
      let codifiedVariable = '';
      variablesForAttribute.forEach((objectPair) => {
        if (objectPair.title === searchVar) {
          codifiedVariable = objectPair.value;
          if (
            ['cis', 'rog', 'ari', 'pri', 'loc'].includes(
              attribute.substring(0, 3)
            )
          ) {
            // catch all text based var_ids such as 'pri_culture_diversity_index',
            // 'pri_culture_diversity_index', 'pri_urbanity', 'pri_social_group', 'pri_lifestage',
            // 'loc_fsa', 'loc_mcity', 'ari_segment', 'rog_segment', 'cis_segment', ...
            codifiedVariable = '"' + codifiedVariable + '"';
          }
        }
      });
      return codifiedVariable;
    });
    return codifiedVariables;
  }

  const setQbFields = (qbFields) => {
    dispatch({ type: 'SET_QBFIELDS', payload: qbFields });
  };

  ////////////
  // Tracking

  /**
   * Captures an event for tracking purposes. If the event is a workflow start event,
   * it generates a workflowId and stores it. If it's a workflow end event, it clears
   * the stored workflowId after sending the event.
   *
   * @param {string} eventName - The name of the event to track
   * @param {Object} extraProperties - Additional properties to include with the event
   * @returns {Object} - Returns the event properties
   */
  const captureEvent = (eventName, extraProperties = {}) => {
    let workflowId = state.workflowId;

    // Workflow IDs identify a sequence of events. We start a sequence on START and end it on EXIT.
    // So we set/unset the workflow ID on START and EXIT.
    if (eventName === EVENT_NAMES.AUDIENCEBUILDER_START) {
      workflowId = uuidv4();
      dispatch({ type: 'SET_WORKFLOW_ID', payload: workflowId });
    } else if (
      eventName === EVENT_NAMES.AUDIENCEBUILDER_EXIT ||
      eventName === EVENT_NAMES.AUDIENCEBUILDER_COMPLETE
    ) {
      dispatch({ type: 'CLEAR_WORKFLOW_ID' }); // clear for next events
    }

    // Capture the event
    const eventProps = {
      workflow_id: workflowId,
      ...extraProperties,
    };

    if (eventProps.workflow_id) {
      // Only capture events if we have a workflow ID
      // Audience Crosstab and Behavior Discovery both have an AB, but we don't
      // want to track those here
      trackerService.captureEvent(eventName, authContext.userInfo, eventProps);
    }
    return eventProps;
  };

  return (
    <BuilderContextNew.Provider
      value={{
        groups: state.groups,
        conditions: state.conditions,
        operators: state.operators,
        qbFields: state.qbFields,
        showError: state.showError,
        sqlWhere: state.sqlWhere,
        advanced: state.advanced,
        workflowId: state.workflowId,
        state,
        setFields,
        setGroups,
        setQbFields,
        refreshAdvancedQB,
        addCondition,
        removeGroup,
        removeCondition,
        modifyCondition,
        setLogicalOperator,
        deleteLogicalOperator,
        setAudienceBuilderType,
        addGroup,
        updateOperator,
        getSQLWhere,
        setDisplay,
        resetAdvancedQB,
        captureEvent,
      }}
    >
      {props.children}
    </BuilderContextNew.Provider>
  );
}

export default BuilderStateNew;
